import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Toast from '../../Toast';
import workflowService from '../../../services/workflowService';

const inputClass =
  'w-full bg-shell border border-shell-card-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-muted-faint outline-none focus:border-primary/40';

/**
 * Create / Edit Workflow modal (dark overlay style).
 * API unchanged:
 *  - Create → workflowService.createWorkflow → POST /workflow/create
 *  - Edit   → workflowService.updateWorkflow → PUT  /workflow/update
 */
const CreateWorkflowModal = ({ onClose, isEdit }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState(isEdit?.name || '');
  const [description, setDescription] = useState(isEdit?.description || '');

  const mutation = useMutation({
    mutationFn: async e => {
      if (isEdit) {
        const editPayload = {
          ...e,
          workflow_id: isEdit?.workflow_id,
        };
        return await workflowService.updateWorkflow(editPayload);
      }
      const res = await workflowService.createWorkflow({
        ...e,
        steps: [],
      });
      navigate(`/workflows/${res?.id}`);
      return res;
    },
    onSuccess: response => {
      Toast({
        type: 'success',
        message: response.detail,
      });
      queryClient.invalidateQueries({ queryKey: ['get_all_workflows'] });
      onClose();
    },
    onError: err => {
      Toast({
        type: 'error',
        message: err.detail,
      });
    },
  });

  const handleSubmit = () => {
    if (!name.trim() || !description.trim()) return;
    mutation.mutate({ name: name.trim(), description: description.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-shell-card border border-shell-card-border rounded-xl p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-white mb-4">{isEdit ? 'Edit Workflow' : 'Create a Workflow'}</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted mb-1.5 block">Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Example: Quarterly Planning & Research"
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-sm text-muted mb-1.5 block">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="This workflow is a collection of threads related to planning..."
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted hover:text-white">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!name.trim() || !description.trim() || mutation.isPending}
            className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-black rounded-lg disabled:opacity-50">
            {mutation.isPending
              ? isEdit
                ? 'Saving...'
                : 'Creating...'
              : isEdit
                ? 'Save Changes'
                : 'Create Workflow'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkflowModal;
